// Live app state, fed by Firestore listeners.
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from './firebase.js';
import { ymd, DRIVER_COLORS, driversFor, resolveOptions } from './util.js';

export const S = {
  user: null, email: '', me: null, // me = staff record
  date: ymd(), view: 'dispatch', tab: 'today',
  lab: { name: 'Watson Dental Laboratory', address: '131 W Austin Ave, Hutto, TX 78634', lat: 30.5427, lng: -97.5467, startTime: '08:30', endTime: '18:00', returnToLab: true, routeDrivers: {} },
  staff: [], offices: {}, stops: [], routes: {}, tracks: {}, day: {}, splitSlots: {},
  msgs: [], reads: {}, chatWith: null, // messages: newest last; reads: thread -> time I last read it
  loaded: { stops: false, offices: false, staff: false },
  busy: {},
};

const listeners = new Set();
export const onChange = (fn) => listeners.add(fn);
let queued = false;
export function emit() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => { queued = false; listeners.forEach((fn) => fn()); });
}

const deliveredHooks = new Set();
const messageHooks = new Set();
export const onMessage = (fn) => messageHooks.add(fn);
export const onDelivered = (fn) => deliveredHooks.add(fn);

let base = []; let day = [];
function stopAll(list) { list.forEach((u) => u()); list.length = 0; }
const onErr = (what) => (e) => console.warn(`Listener ${what} failed`, e);

export function startBase() {
  stopAll(base);
  base.push(onSnapshot(doc(db, 'config/lab'), (s) => { if (s.exists()) S.lab = { ...S.lab, ...s.data() }; emit(); }, onErr('config')));
  base.push(onSnapshot(collection(db, 'staff'), (q) => {
    S.staff = q.docs.map((d) => ({ email: d.id, ...d.data() })).sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
    S.me = S.staff.find((x) => x.email === S.email) || S.me;
    S.loaded.staff = true; emit();
  }, onErr('staff')));
  base.push(onSnapshot(collection(db, 'offices'), (q) => {
    const o = {}; q.docs.forEach((d) => { o[d.id] = { id: d.id, ...d.data() }; });
    S.offices = o; S.loaded.offices = true; emit();
  }, onErr('offices')));
  startMessages();
}

// ----- messages -----
// One conversation per driver (thread = their email) plus 'all' for everyone on the team.
// Dispatch sees every thread from the last two weeks; a driver sees only their own and 'all'.
const MSG_DAYS = 14;
let msgSeen = null;
function startMessages() {
  const mine = new Map(); // listener key -> messages
  const take = (key) => (q) => {
    mine.set(key, q.docs.map((d) => ({ id: d.id, ...d.data() })));
    const all = [...new Map([...mine.values()].flat().map((m) => [m.id, m])).values()].sort((a, b) => (a.at || 0) - (b.at || 0));
    if (msgSeen) for (const m of all) if (!msgSeen.has(m.id) && m.from !== S.email) messageHooks.forEach((fn) => fn(m));
    msgSeen = new Set(all.map((m) => m.id));
    S.msgs = all; emit();
  };
  const since = Date.now() - MSG_DAYS * 86400000;
  if (['admin', 'shipping', 'dispatch'].includes(S.me?.role)) {
    base.push(onSnapshot(query(collection(db, 'messages'), where('at', '>=', since)), take('dispatch'), onErr('messages')));
  } else {
    base.push(onSnapshot(query(collection(db, 'messages'), where('thread', '==', S.email)), take('mine'), onErr('messages')));
    base.push(onSnapshot(query(collection(db, 'messages'), where('thread', '==', 'all')), take('all'), onErr('messages all')));
  }
  base.push(onSnapshot(doc(db, 'reads', S.email), (s) => { S.reads = s.exists() ? s.data() : {}; emit(); }, onErr('reads')));
}
/** Messages in one conversation. A driver's own view merges their thread with 'all'. */
export const threadMsgs = (thread) => S.msgs.filter((m) => m.thread === thread);
/** Unread messages from other people, in one thread or (no argument) everywhere I can see. */
export function unreadCount(thread) {
  return S.msgs.filter((m) => (thread == null || m.thread === thread) && m.from !== S.email && (m.at || 0) > (S.reads[m.thread] || 0)).length;
}

let seen = null;
export function startDay() {
  stopAll(day);
  S.stops = []; S.routes = {}; S.tracks = {}; S.day = {}; S.splitSlots = {}; S.loaded.stops = false; seen = null;
  const date = S.date;
  day.push(onSnapshot(query(collection(db, 'stops'), where('date', '==', date)), (q) => {
    S.stops = q.docs.map((d) => ({ id: d.id, ...d.data() }));
    S.loaded.stops = true;
    const now = new Set(S.stops.filter((s) => s.status !== 'pending').map((s) => s.id));
    if (seen) for (const id of now) if (!seen.has(id)) deliveredHooks.forEach((fn) => fn(S.stops.find((s) => s.id === id)));
    seen = now;
    emit();
  }, onErr('stops')));
  day.push(onSnapshot(doc(db, 'days', date), (s) => { S.day = s.exists() ? s.data() : {}; emit(); }, onErr('day')));
  day.push(onSnapshot(query(collection(db, 'routes'), where('date', '==', date)), (q) => {
    const r = {}; q.docs.forEach((d) => { r[d.get('driverEmail')] = { id: d.id, ...d.data() }; });
    S.routes = r; emit();
  }, onErr('routes')));
  day.push(onSnapshot(query(collection(db, 'tracks'), where('date', '==', date)), (q) => {
    const t = {}; q.docs.forEach((d) => { t[d.get('driverEmail')] = d.get('points') || []; });
    S.tracks = t; emit();
  }, onErr('tracks')));
}

export function stopListening() { stopAll(base); stopAll(day); }

// ----- derived -----
export const isDispatch = () => ['admin', 'shipping', 'dispatch'].includes(S.me?.role);
export const isAdmin = () => S.me?.role === 'admin';
/** Admins and shipping managers: everything, including lab settings. Only admins add or edit people. */
export const isManager = () => ['admin', 'shipping'].includes(S.me?.role);
export const drivers = () => S.staff.filter((x) => x.active && (x.role === 'driver' || x.drives));
/** Drivers with at least one stop on the day being viewed (most staff can drive, few do on a given day). */
export const workingDrivers = () => drivers().filter((d) => S.stops.some((s) => s.driverEmail === d.email));
export function driverColor(email) {
  // Colors go to the day's working drivers first, so two people on the road never share one.
  const i = workingDrivers().findIndex((d) => d.email === email);
  return i < 0 ? '#9ca3af' : DRIVER_COLORS[i % DRIVER_COLORS.length];
}
export const driverName = (email) => S.staff.find((x) => x.email === email)?.name || email || 'Unassigned';
export const driverStops = (email) => S.stops.filter((s) => s.driverEmail === email).sort((a, b) => (a.seq ?? 999) - (b.seq ?? 999));
export const unassigned = () => S.stops.filter((s) => !s.driverEmail || !drivers().some((d) => d.email === s.driverEmail));
export const officeOf = (s) => S.offices[s.officeId];
export const officeName = (s) => officeOf(s)?.name || s.officeName || 'Unknown office';
export function stopPoint(s) {
  const o = officeOf(s);
  return o && typeof o.lat === 'number' ? { lat: o.lat, lng: o.lng } : null;
}
/** The Pilot route a stop belongs to (hand-added stops use their office's route). */
export const routeOf = (s) => s.route || officeOf(s)?.route || '';
/** Drivers on a route today: today's board, or the default in Settings. */
export const dayDrivers = (route) => driversFor(route, S.day.routeDrivers, S.lab.routeDrivers);
/** True when today's board hasn't touched this route, so the Settings default applies. */
export const usingDefault = (route) => !Object.prototype.hasOwnProperty.call(S.day.routeDrivers || {}, route);
/** Options a driver's route uses on the day being viewed: their day choices, their own defaults, then the lab's. */
export const routeOpt = (email) => resolveOptions({ lab: S.lab, day: S.day, staff: S.staff.find((x) => x.email === email) || {}, route: S.routes[email] || {} });
export const endsAtLab = (email) => routeOpt(email).returnToLab;
export const isToday = () => S.date === ymd();
export const routeKey = (email) => `${S.date}_${email}`;
